import { type FC, type ReactNode } from 'react';
import { Accordion, Island, VStack } from '@direct-frontend/components';
import { type OpenApiSchemaDashboardTodolistItem } from '#shared/api/schema';
import { PageStub } from '#shared/components/PageStub';
import { DashboardTodoListStepNavigation } from './components/DashboardTodoListStepNavigation/DashboardTodoListStepNavigation';
import { useDashboardTodoList } from './hooks/useDashboardTodoList';
import { type DashboardTodoListStepNavigationState } from './lib/dashboardTodoListStepNavigation';
import { DashboardTodoListContent } from './DashboardTodoListContent';
import { DashboardTodoListFeatureHintsPendingRegistration } from './DashboardTodoListFeatureHintsPendingRegistration';

function renderDashboardTodoListAccordionBody(params: {
    isMainExpanded: boolean;
    isLoading: boolean;
    sortedItems: OpenApiSchemaDashboardTodolistItem[] | null | undefined;
    itemsKey: string;
    completingItemId: string | null;
    onToggleItemComplete: (item: OpenApiSchemaDashboardTodolistItem) => void;
    onMarkdownLinkClick: (params: { itemId: string; href: string }) => void;
    stepNavigation: DashboardTodoListStepNavigationState | null;
    isMovingBackward: boolean;
    isMovingForward: boolean;
    onMoveBackward: () => void;
    onMoveForward: () => void;
}): ReactNode {
    const {
        isMainExpanded,
        isLoading,
        sortedItems,
        itemsKey,
        completingItemId,
        onToggleItemComplete,
        onMarkdownLinkClick,
        stepNavigation,
        isMovingBackward,
        isMovingForward,
        onMoveBackward,
        onMoveForward,
    } = params;

    if (!isMainExpanded) {
        return null;
    }

    if (!isLoading && sortedItems != null) {
        return (
            <VStack gap="12">
                <DashboardTodoListContent
                    key={itemsKey}
                    completingItemId={completingItemId}
                    items={sortedItems}
                    onMarkdownLinkClick={onMarkdownLinkClick}
                    onToggleItemComplete={onToggleItemComplete}
                />
                {stepNavigation == null ? null : (
                    <DashboardTodoListStepNavigation
                        {...stepNavigation}
                        isMovingBackward={isMovingBackward}
                        isMovingForward={isMovingForward}
                        onMoveBackward={onMoveBackward}
                        onMoveForward={onMoveForward}
                    />
                )}
            </VStack>
        );
    }

    return (
        <>
            <DashboardTodoListFeatureHintsPendingRegistration />
            <PageStub />
        </>
    );
}

export const DashboardTodoList: FC = () => {
    const {
        isMainExpanded,
        onMainExpandToggle,
        completingItemId,
        onToggleItemComplete,
        sortedItems,
        isLoading,
        shouldHideWidget,
        itemsKey,
        title,
        description,
        stepNavigation,
        isMovingBackward,
        isMovingForward,
        onMoveBackward,
        onMoveForward,
        onMarkdownLinkClick,
    } = useDashboardTodoList();

    if (shouldHideWidget) {
        return null;
    }

    const accordionBody = renderDashboardTodoListAccordionBody({
        isMainExpanded,
        isLoading,
        sortedItems,
        itemsKey,
        completingItemId,
        onToggleItemComplete,
        onMarkdownLinkClick,
        stepNavigation,
        isMovingBackward,
        isMovingForward,
        onMoveBackward,
        onMoveForward,
    });

    return (
        <Island paddingCorrection="top-bottom">
            <VStack gap="28">
                <Accordion
                    caption={description || undefined}
                    data-testid="DashboardTodoList"
                    isExpanded={isMainExpanded}
                    level="h2"
                    title={title}
                    type="right"
                    onExpandToggle={onMainExpandToggle}
                >
                    {accordionBody}
                </Accordion>
            </VStack>
        </Island>
    );
};
